import axios from 'axios';
import { CheckFunction } from '../layouts/MainTenantconfig';
import { USE_TOKEN_AUTH } from './apiHeaders';
import { BASE_URL } from './api-factory';
import { encryptAES } from './Encrypt';

// for reviing 401 if not for any other error return repsonse same for success need to do that

// ---------------------------------------------------------------------------
// PER-TENANT TOKEN CONFIG
// Key = the tenant's MAIN service BASE_URL (whatever CheckFunction() returns
// as configInfo.BASE_URL for that tenant).
// Value = the exact list of URLs (main + whichever infitouch sub-services)
// that should get the access_token header FOR THAT TENANT.
//
// Add/remove entries per company as needed — nothing else in the file needs
// to change when you onboard a new tenant.
// ---------------------------------------------------------------------------
const TENANT_TOKEN_CONFIG = {
    'https://backend.credfin.money/': [
        'https://backend.credfin.money/',
        // 'https://finance.infitouch.in/',
        // 'https://icommunication.infitouch.in/',
        // 'https://bre.infitouch.in/',
        // 'https://paymentservice.infitouch.in/'
    ],
    'https://backend.abhicash.in/': [
        // 'https://backend.abhicash.in/',
        // 'https://finance.infitouch.in/',
        // 'https://bre.infitouch.in/',
        // 'https://icommunication.infitouch.in/',
        // 'https://paymentservice.infitouch.in/'
    ],
    'https://backend.creditjar.in/': [
        // 'https://backend.creditjar.in/',
        // 'https://finance.infitouch.in/',
        // 'https://bre.infitouch.in/',
        // 'https://icommunication.infitouch.in/',
        // 'https://paymentservice.infitouch.in/'
    ],
    'https://backend.cashbridge.in/': [
        // 'https://backend.cashbridge.in/',
        // 'https://finance.infitouch.in/',
        // 'https://bre.infitouch.in/',
        // 'https://icommunication.infitouch.in/',
        // 'https://paymentservice.infitouch.in/'
    ],
};

try {
    // Resolve current tenant's main BASE_URL + its allowed-for-token list once
    // per call (cheap — CheckFunction() is presumably a fast lookup/state read).
    const getTenantContext = () => {
        const configInfo = CheckFunction();
        const mainServiceUrl = configInfo && configInfo.BASE_URL ? configInfo.BASE_URL : null;
        let allowedUrls = mainServiceUrl && TENANT_TOKEN_CONFIG[mainServiceUrl]
            ? [...TENANT_TOKEN_CONFIG[mainServiceUrl]]
            : [];
        
        // Dynamically add the currently configured/overridden BASE_URL from api-factory
        // to the allowed list so that local development URLs also receive the token.
        if (BASE_URL && mainServiceUrl && allowedUrls.includes(mainServiceUrl)) {
            if (!allowedUrls.includes(BASE_URL)) {
                allowedUrls.push(BASE_URL);
            }
        }
        return { configInfo, mainServiceUrl, allowedUrls };
    };

    // Does this url belong to the tenant's main service itself (used to decide logout)?
    // IMPORTANT: this is only meaningful for logout purposes if the tenant's
    // config actually enables token auth for the main service. If the main
    // service URL isn't in that tenant's allowedUrls, we never treat its
    // 401s as "token expired" — some other auth mechanism (session/cookie)
    // may be in play there, and it's not this interceptor's job to log out
    // for it.
    const isMainServiceUrl = (url, mainServiceUrl, allowedUrls) => {
        if (!mainServiceUrl) return false;
        if (!url) return false;
        if (typeof url !== 'string') return false;
        if (!allowedUrls || !allowedUrls.includes(mainServiceUrl)) return false;

        // Relative / same-origin calls are treated as hitting the main service.
        if (url.startsWith('/') || (!url.startsWith('http://') && !url.startsWith('https://'))) {
            return true;
        }
        if (url.startsWith(window.location.origin)) {
            return true;
        }
        return url.startsWith(mainServiceUrl);
    };

    // Is this url one of the tenant-specific allowed-for-token urls?
    const isTargetUrl = (url, allowedUrls, mainServiceUrl) => {
        if (!url) return false;
        if (typeof url !== 'string') return false;

        // Relative paths / same-origin -> treat as main service, only allow
        // if the main service itself is in this tenant's allowed list.
        if (url.startsWith('/') || (!url.startsWith('http://') && !url.startsWith('https://'))) {
            return mainServiceUrl ? allowedUrls.includes(mainServiceUrl) : false;
        }
        if (url.startsWith(window.location.origin)) {
            return mainServiceUrl ? allowedUrls.includes(mainServiceUrl) : false;
        }

        return allowedUrls.some((baseUrl) => url.startsWith(baseUrl));
    };

    // Public endpoints that must NOT have custom headers (to prevent CORS preflight failures)
    const isPublicUrl = (url) => {
        if (!url) return false;
        if (typeof url !== 'string') return false;
        return (
            url.includes('generateRandonCaptcha') ||
            url.includes('adminrole') ||
            url.includes('adminLogin') ||
            url.includes('adminRefreshToken')
            // url.includes('oauth/token')
            // url.includes('getAdminUser') ||
            // url.includes('getAllStatus') ||
            // url.includes('getNotificationDetails')
        );
    };

    // Clean headers of undefined/null values that cause fetch to crash
    const sanitizeHeaders = (headers) => {
        if (!headers) return headers;
        try {
            if (headers instanceof Headers) {
                const keysToDelete = [];
                headers.forEach((value, key) => {
                    if (value === undefined || value === null || value === 'undefined' || value === 'null') {
                        keysToDelete.push(key);
                    }
                });
                keysToDelete.forEach(key => headers.delete(key));
            } else if (Array.isArray(headers)) {
                return headers.filter(([key, value]) => {
                    return value !== undefined && value !== null && value !== 'undefined' && value !== 'null';
                });
            } else if (typeof headers === 'object') {
                for (const key in headers) {
                    if (headers[key] === undefined || headers[key] === null || headers[key] === 'undefined' || headers[key] === 'null') {
                        delete headers[key];
                    }
                }
            }
        } catch (e) {
            console.error("Error sanitizing headers:", e);
        }
        return headers;
    };

    // Helper to determine if we should pass token headers for a request,
    // scoped to the current tenant's allowed list.
    const shouldInjectToken = (url, allowedUrls, mainServiceUrl) => {
        if (!USE_TOKEN_AUTH) return false;
        return isTargetUrl(url, allowedUrls, mainServiceUrl) && !isPublicUrl(url);
    };

    // Helper to perform logout and page refresh
    const handleLogout = () => {
        window.localStorage.setItem('status', 'false');
        window.localStorage.removeItem('AdminemailId');
        window.localStorage.removeItem('AdminRole');
        window.localStorage.removeItem('AdminRoleList');
        window.localStorage.removeItem('password');
        window.localStorage.removeItem('access_token');
        window.localStorage.removeItem('refresh_token');
        window.location.reload();
    };

    // Guard so a burst of concurrent 401s only triggers one logout/reload
    let logoutInFlight = false;
    const triggerLogoutOnce = () => {
        if (logoutInFlight) return;
        logoutInFlight = true;
        handleLogout();
    };

    // Token refresh queue and state
    let isRefreshing = false;
    let failedQueue = [];

    const processQueue = (error, token = null) => {
        failedQueue.forEach(prom => {
            if (error) {
                prom.reject(error);
            } else {
                prom.resolve(token);
            }
        });
        failedQueue = [];
    };

    // Refresh Token API call using stored refresh_token
    const refreshToken = () => {
        return new Promise((resolve, reject) => {
            const storedRefreshToken = window.localStorage.getItem('refresh_token');
            if (!storedRefreshToken) {
                triggerLogoutOnce();
                return reject(new Error('No refresh token available'));
            }

            const encryptedData = encryptAES({ refreshToken: storedRefreshToken });
            const data = { data: encryptedData };

            const tokenUrl = BASE_URL ? BASE_URL + 'adminRefreshToken' : 'https://backend.credfin.money/adminRefreshToken';

            originalFetch(tokenUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            })
            .then(res => {
                if (res.status === 401) {
                    // If refresh token request itself returns 401, trigger logout
                    triggerLogoutOnce();
                    return reject(new Error('Refresh token expired'));
                }
                if (!res.ok) {
                    throw new Error('Refresh token request failed');
                }
                return res.json();
            })
            .then(json => {
                if (json && json.status === 'success' && json.token) {
                    window.localStorage.setItem('access_token', json.token);
                    if (json.refreshToken) {
                        window.localStorage.setItem('refresh_token', json.refreshToken);
                    }
                    resolve(json.token);
                } else {
                    triggerLogoutOnce();
                    reject(new Error('Invalid token response'));
                }
            })
            .catch(err => {
                reject(err);
            });
        });
    };

    // Helper to inject token header value into Fetch request init object
    const setFetchTokenHeader = (init, token) => {
        init = init ? { ...init } : {};
        let headers = init.headers || {};
        if (typeof headers === 'object' && headers !== null) {
            headers = { ...headers };
        }
        if (headers instanceof Headers) {
            headers.set('token', `bearer ${token}`);
        } else if (Array.isArray(headers)) {
            const index = headers.findIndex(([key]) => key.toLowerCase() === 'token');
            if (index !== -1) {
                headers[index] = ['token', `bearer ${token}`];
            } else {
                headers.push(['token', `bearer ${token}`]);
            }
        } else {
            headers['token'] = `bearer ${token}`;
        }
        init.headers = sanitizeHeaders(headers);
        return init;
    };

    // --- GLOBAL AXIOS REQUEST INTERCEPTOR ---
    axios.interceptors.request.use(
        (config) => {
            try {
                // Clone headers to prevent mutation errors on read-only objects
                if (config.headers) {
                    config.headers = { ...config.headers };
                }

                const { allowedUrls, mainServiceUrl } = getTenantContext();

                if (config.url && shouldInjectToken(config.url, allowedUrls, mainServiceUrl)) {
                    config.headers = config.headers || {};

                    const token = window.localStorage.getItem('access_token');
                    if (token) {
                        if (!config.headers['token']) {
                            config.headers['token'] = `bearer ${token}`;
                        }
                    }
                }

                // Clean up any undefined headers to prevent crashes
                if (config.headers) {
                    config.headers = sanitizeHeaders(config.headers);
                }
            } catch (err) {
                console.error("Error in Axios request interceptor:", err);
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // --- GLOBAL AXIOS RESPONSE INTERCEPTOR ---
    axios.interceptors.response.use(
        (response) => {
            try {
                const url = response.config ? response.config.url : undefined;
                if (url && !isPublicUrl(url) && !url.includes('oauth/token')) {
                    // Check for the soft 401 response body
                    if (response.data && (response.data.status === 401 || response.data.status === '401')) {
                        const originalRequest = response.config;
                        if (!originalRequest._retry) {
                            originalRequest._retry = true;
                            if (isRefreshing) {
                                return new Promise((resolve, reject) => {
                                    failedQueue.push({ resolve, reject });
                                }).then(token => {
                                    originalRequest.headers['token'] = `bearer ${token}`;
                                    return axios(originalRequest);
                                });
                            }

                            isRefreshing = true;
                            return new Promise((resolve, reject) => {
                                refreshToken()
                                    .then(token => {
                                        originalRequest.headers['token'] = `bearer ${token}`;
                                        processQueue(null, token);
                                        resolve(axios(originalRequest));
                                    })
                                    .catch(err => {
                                        processQueue(err, null);
                                        reject(err);
                                    })
                                    .finally(() => {
                                        isRefreshing = false;
                                    });
                            });
                        }
                    }
                }
            } catch (err) {
                console.error("Error in Axios response success handling:", err);
            }
            return response;
        },
        (error) => {
            try {
                const originalRequest = error.config;
                const url = originalRequest ? originalRequest.url : undefined;
                const httpStatus = error.response ? error.response.status : undefined;

                if (httpStatus === 401 && originalRequest && !originalRequest._retry) {
                    if (isPublicUrl(url) || (url && url.includes('oauth/token'))) {
                        return Promise.reject(error);
                    }

                    originalRequest._retry = true;

                    if (isRefreshing) {
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject });
                        }).then(token => {
                            originalRequest.headers['token'] = `bearer ${token}`;
                            return axios(originalRequest);
                        }).catch(err => {
                            return Promise.reject(err);
                        });
                    }

                    isRefreshing = true;
                    return new Promise((resolve, reject) => {
                        refreshToken()
                            .then(token => {
                                originalRequest.headers['token'] = `bearer ${token}`;
                                processQueue(null, token);
                                resolve(axios(originalRequest));
                            })
                            .catch(err => {
                                processQueue(err, null);
                                reject(err);
                            })
                            .finally(() => {
                                isRefreshing = false;
                            });
                    });
                }
            } catch (err) {
                console.error("Error in Axios response error handling:", err);
            }
            return Promise.reject(error);
        }
    );

    // --- GLOBAL FETCH INTERCEPTOR ---
    const originalFetch = window.fetch;
    window.fetch = async function (resource, init) {
        let url = "";
        try {
            if (typeof resource === 'string') {
                url = resource;
            } else if (resource && resource.url) {
                url = resource.url;
            }

            const { allowedUrls, mainServiceUrl } = getTenantContext();

            if (url && shouldInjectToken(url, allowedUrls, mainServiceUrl)) {
                // Clone init to prevent read-only getter assignment errors
                init = init ? { ...init } : {};

                let headers = init.headers || {};

                // If headers is the dual-header itself or has getters, clone it to a plain object
                if (typeof headers === 'object' && headers !== null) {
                    headers = { ...headers };
                }

                const token = window.localStorage.getItem('access_token');

                if (headers instanceof Headers) {
                    if (token) {
                        if (!headers.has('token')) {
                            headers.set('token', `bearer ${token}`);
                        }
                    }
                } else if (Array.isArray(headers)) {
                    if (token) {
                        const hasAuth = headers.some(([key]) => key.toLowerCase() === 'token');
                        if (!hasAuth) {
                            headers.push(['token', `bearer ${token}`]);
                        }
                    }
                } else {
                    if (token) {
                        if (!headers['token'] && !headers['token']) {
                            headers['token'] = `bearer ${token}`;
                        }
                    }
                }

                init.headers = sanitizeHeaders(headers);
            } else if (init && init.headers) {
                // Clone init to prevent read-only mutation on public/external routes
                init = { ...init };
                if (typeof init.headers === 'object' && init.headers !== null) {
                    init.headers = { ...init.headers };
                }
                init.headers = sanitizeHeaders(init.headers);
            }
        } catch (err) {
            console.error("Error in Fetch request interceptor:", err);
        }

        try {
            let response = await originalFetch(resource, init);

            // Check if this response represents a soft 401 body contract
            const isSoft401 = async () => {
                if (response && response.status >= 200 && response.status < 300) {
                    const { allowedUrls, mainServiceUrl } = getTenantContext();
                    const isRelevant = isMainServiceUrl(url, mainServiceUrl, allowedUrls) || isTargetUrl(url, allowedUrls, mainServiceUrl);
                    if (isRelevant) {
                        try {
                            const cloned = response.clone();
                            const contentType = cloned.headers.get('content-type') || '';
                            if (contentType.includes('application/json')) {
                                const data = await cloned.json();
                                if (data && (data.status === 401 || data.status === '401')) {
                                    return true;
                                }
                            }
                        } catch (parseErr) {}
                    }
                }
                return false;
            };

            const isAuthFailure = (response && response.status === 401) || await isSoft401();

            if (isAuthFailure) {
                if (isPublicUrl(url) || url.includes('oauth/token')) {
                    return response;
                }

                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    })
                    .then(token => {
                        const newInit = setFetchTokenHeader(init, token);
                        return originalFetch(resource, newInit);
                    });
                }

                isRefreshing = true;

                try {
                    const token = await refreshToken();
                    isRefreshing = false;
                    processQueue(null, token);

                    const newInit = setFetchTokenHeader(init, token);
                    return originalFetch(resource, newInit);
                } catch (err) {
                    isRefreshing = false;
                    processQueue(err, null);
                    throw err;
                }
            }

            return response;
        } catch (error) {
            throw error;
        }
    };

    console.log("Global fetch & axios Token API interceptors initialized successfully (multi-tenant).");
} catch (e) {
    console.error("Failed to initialize Token API interceptors:", e);
}